import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { listStores } from '../services/storeService'

type OrganisationDisplayContextValue = {
  organisationDisplayName: string | null
  loading: boolean
}

const OrganisationDisplayContext = createContext<OrganisationDisplayContextValue | null>(null)

export function OrganisationDisplayProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const fromUser = user?.organisationName ?? user?.organisation?.name ?? null
  const [fallbackName, setFallbackName] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!user) setFallbackName(null)
  }, [user])

  useEffect(() => {
    if (fromUser) return
    if (user?.organisationId == null) return

    let cancelled = false
    setLoading(true)
    listStores()
      .then(({ items }) => {
        const n = items[0]?.organisation?.name
        if (!cancelled && n) setFallbackName(n)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [fromUser, user?.organisationId])

  const organisationDisplayName = fromUser || fallbackName

  const value = useMemo(
    () => ({ organisationDisplayName, loading }),
    [organisationDisplayName, loading],
  )

  return (
    <OrganisationDisplayContext.Provider value={value}>{children}</OrganisationDisplayContext.Provider>
  )
}

export function useOrganisationDisplayName() {
  const ctx = useContext(OrganisationDisplayContext)
  if (!ctx) {
    throw new Error('useOrganisationDisplayName must be used within OrganisationDisplayProvider')
  }
  return ctx
}
